import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { CaretLeft, Crown, CreditCard, Coins, Storefront, Sparkle, ArrowsLeftRight, InstagramLogo, TiktokLogo, DownloadSimple, Share, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BRAND, BRAND_ASSETS } from '../config/programs';
import { generateVipShareImage } from '../lib/vipShareImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VIPCardPage() {
  const { t, tn } = useLanguage();
  const navigate = useNavigate();
  const [vipStatus, setVipStatus] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const [storyPlatform, setStoryPlatform] = useState(null);
  const [videoLink, setVideoLink] = useState('');
  const [activationSent, setActivationSent] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [shareImageBlob, setShareImageBlob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [myCars, setMyCars] = useState([]);
  // Iter31 FIX 9 — VIP card number self-service entry.
  const [vipNumberDraft, setVipNumberDraft] = useState('');
  const [vipNumberSaved, setVipNumberSaved] = useState('');
  const [vipNumberSaving, setVipNumberSaving] = useState(false);
  // Card back — textual program list (replaces the outdated static PNG)
  const [backPrograms, setBackPrograms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/vip/status`, { withCredentials: true })
      .then(res => setVipStatus(res.data))
      .catch(console.error);
    axios.get(`${API}/vip/activation-status`, { withCredentials: true })
      .then(res => { if (res.data?.activated) setActivationSent(true); })
      .catch(() => {});
    axios.get(`${API}/cars`, { withCredentials: true })
      .then(res => setMyCars(res.data || []))
      .catch(() => {});
    axios.get(`${API}/wash-programs`)
      .then(res => setBackPrograms(res.data || []))
      .catch(() => {});
    // Iter31 FIX 9 — Read current VIP card number from /auth/me for self-service edit.
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        const n = res.data?.vip_card_number || '';
        setVipNumberDraft(n);
        setVipNumberSaved(n);
      })
      .catch(() => {});
  }, []);

  const saveVipNumber = async () => {
    setVipNumberSaving(true);
    try {
      const value = (vipNumberDraft || '').trim();
      const r = await axios.put(`${API}/users/me`,
        { vip_card_number: value || '' },
        { withCredentials: true },
      );
      const next = r.data?.vip_card_number || '';
      setVipNumberDraft(next);
      setVipNumberSaved(next);
      toast.success(t('vipCardNumberSaved') || 'Numri i kartës u ruajt');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Gabim';
      toast.error(msg);
    } finally {
      setVipNumberSaving(false);
    }
  };

  const generatePreview = useCallback(async () => {
    setGenerating(true);
    try {
      const plate = myCars[0]?.license_plate || 'AA 000 AA';
      const blob = await generateVipShareImage({
        plate,
        headline: t('vipShareHeadline'),
        logoUrl: BRAND_ASSETS.logo,
      });
      const url = URL.createObjectURL(blob);
      setShareImageBlob(blob);
      setShareImageUrl(url);
    } finally {
      setGenerating(false);
    }
  }, [myCars, t]);

  const onPickPlatform = useCallback((platform) => {
    setStoryPlatform(platform);
    // Auto-generate the share image the moment the user picks a platform
    generatePreview();
  }, [generatePreview]);

  const tryWebShare = useCallback(async () => {
    if (!shareImageBlob) return;
    const file = new File([shareImageBlob], 'autospa5-vip.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: t('vipShareHeadline'), text: t('vipShareHeadline') });
      } catch (_) { /* user dismissed */ }
    } else {
      // Fallback: download the image so the user can manually upload it as a story
      const a = document.createElement('a');
      a.href = shareImageUrl;
      a.download = 'autospa5-vip.png';
      a.click();
    }
  }, [shareImageBlob, shareImageUrl, t]);

  const handleStoryPosted = async () => {
    try {
      await axios.post(`${API}/vip/activate-story`, {
        platform: storyPlatform,
        video_link: videoLink || null,
      }, { withCredentials: true });
      setActivationSent(true);
    } catch (err) {
      console.error(err);
    }
  };

  const progressPercent = vipStatus ? (vipStatus.progress_to_next_wash / (vipStatus.target || 7000)) * 100 : 0;

  const howItWorks = [
    // In-app card payment entry REMOVED — Stripe not available for Albanian entities.
    { icon: Storefront, title: t('vipPaymentTerminal'), desc: t('vipPaymentTerminalDesc'), color: BRAND.secondary },
    { icon: Coins, title: t('vipPaymentCash'), desc: t('vipPaymentCashDesc'), color: '#FFD700' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white/60 hover:text-white"
          data-testid="vip-back-btn"
        >
          <CaretLeft size={24} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight font-['Outfit'] text-white flex items-center gap-2">
            <Crown size={26} weight="fill" className="text-[#FFD700]" />
            {t('vipCardTitle')}
          </h1>
          <p className="text-white/50 text-sm mt-0.5">{t('vipCardSubtitle')}</p>
        </div>
      </div>

      {/* Card Flip — vertical/portrait, fills the available width */}
      <div className="px-4 mt-2 flex justify-center">
        <motion.div
          className="relative w-full max-w-[360px] cursor-pointer"
          style={{ aspectRatio: '0.63 / 1' }}
          onClick={() => setShowBack(s => !s)}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.985 }}
          data-testid="vip-card-flip"
        >
          {/* Golden ring glow */}
          <div className="absolute inset-0 rounded-3xl pointer-events-none"
               style={{ boxShadow: `0 0 60px ${BRAND.primaryGlow}, 0 0 160px rgba(255, 215, 0, 0.25)` }} />
          {/* Image: front = object-contain (preserves card aspect ratio).
              Back = portrait — the source image IS already vertical so we just give it
              full height + auto width centred, no rotation.                       */}
          {showBack ? (
            <motion.div
              key="back-wrap"
              className="absolute inset-0 rounded-3xl bg-[#0a0a0a] border border-[#FFD700]/30 overflow-hidden flex flex-col"
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              data-testid="vip-card-back-programs"
            >
              <div className="px-5 pt-5 pb-3 border-b border-[#FFD700]/15 flex items-center gap-2">
                <Crown size={18} weight="fill" className="text-[#FFD700]" />
                <p className="text-sm font-bold tracking-[0.25em] text-[#FFD700]">PROGRAMET</p>
              </div>
              <div className="flex-1 px-5 py-3 flex flex-col justify-evenly">
                {backPrograms.map((p) => (
                  <div
                    key={p.program_id}
                    data-testid={`vip-back-program-${p.program_id}`}
                    className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className={`text-[13px] font-bold uppercase tracking-wide ${p.program_id === 'prog_standard' ? 'text-[#FFD700]' : 'text-white'}`}>
                        {p.name_en}{p.program_id === 'prog_standard' ? ' ⭐' : ''}
                      </p>
                      <p className="text-[11px] text-white/45 truncate">{p.description_sq}</p>
                    </div>
                    <p className="text-base font-bold text-[#FFD700] whitespace-nowrap">{p.price} L</p>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3.5 bg-[#FFD700]/10 border-t border-[#FFD700]/20">
                <p className="text-[11px] text-[#FFD700] font-semibold text-center leading-snug">
                  {t('vipExplain')}
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.img
              key="front"
              src={BRAND_ASSETS.vipCardFront}
              alt="VIP Card Front"
              className="absolute inset-0 w-full h-full rounded-3xl object-contain"
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          )}
          {/* Soft shimmer overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent -translate-x-full animate-[shimmer_4s_infinite] pointer-events-none rounded-3xl" />

          {/* Flip hint */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/15 flex items-center gap-1.5 text-[11px] text-white/80 whitespace-nowrap">
            <ArrowsLeftRight size={12} weight="bold" />
            {showBack ? t('vipCardFront') : t('vipCardBack')}
          </div>
        </motion.div>
      </div>

      {/* Progress to next free wash */}
      {vipStatus && (
        <motion.div
          className="mx-4 mt-5 p-5 rounded-3xl bg-[#0a0a0a] border border-[#FFD700]/20"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#FFD700] uppercase tracking-wider font-bold">{t('progressToNext')}</p>
            {vipStatus.free_washes_available > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-[#FFD700] text-black text-xs font-bold">
                {vipStatus.free_washes_available} {t('freeBadge')}
              </span>
            )}
          </div>
          <div className="flex justify-between items-end mb-2">
            <span className="text-3xl font-bold font-['Outfit'] text-white">
              {vipStatus.progress_to_next_wash.toLocaleString()}
            </span>
            <span className="text-white/40 text-sm">/ {(vipStatus.target || 7000).toLocaleString()} {t('lek')}</span>
          </div>
          <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${BRAND.primary} 0%, #FFD700 100%)`,
                boxShadow: `0 0 12px ${BRAND.primaryGlow}`,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
          {vipStatus.free_washes_available > 0 && (
            <p className="mt-3 text-[#FFD700] text-sm font-medium flex items-center gap-1.5">
              <Sparkle size={14} weight="fill" />
              {tn('freeWashesCount', vipStatus.free_washes_available)}
            </p>
          )}
        </motion.div>
      )}

      {/* Iter31 FIX 9 — VIP card number self-service entry.
          Customer types the number printed on the physical VIP card. */}
      <motion.div
        className="mx-4 mt-4 p-4 rounded-3xl bg-[#0a0a0a] border border-[#FFD700]/20"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        data-testid="vip-card-number-block"
      >
        <p className="text-xs text-[#FFD700] uppercase tracking-wider font-bold mb-2 flex items-center gap-1.5">
          <CreditCard size={14} weight="fill" />
          {t('vipCardNumberLabel') || 'Numri i Kartës VIP'}
        </p>
        <div className="flex gap-2">
          <Input
            value={vipNumberDraft}
            onChange={(e) => setVipNumberDraft(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={t('vipCardNumberPlaceholder') || 'p.sh. 1234567890'}
            className="h-11 flex-1 bg-[#050505] border-white/10 rounded-xl text-sm tabular-nums text-white focus:border-[#FFD700]"
            data-testid="vip-card-number-input"
          />
          <Button
            onClick={saveVipNumber}
            disabled={vipNumberSaving || vipNumberDraft === vipNumberSaved}
            className="h-11 px-3 rounded-xl bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold text-xs disabled:opacity-40"
            data-testid="vip-card-number-save"
          >
            <FloppyDisk size={14} weight="fill" className="mr-1" />
            {vipNumberSaving ? '…' : (t('save') || 'Ruaj')}
          </Button>
        </div>
        <p className="text-[10px] text-white/40 mt-2 leading-snug">
          {t('vipCardNumberHelp') || 'Vendos numrin e printuar në kartën tënde VIP fizike.'}
        </p>
      </motion.div>

      {/* How VIP works */}
      <div className="px-4 mt-5 mb-8">
        <h3 className="text-sm font-bold text-white/60 uppercase tracking-wider mb-3">
          {t('vipHowItWorks')}
        </h3>
        <div className="space-y-2.5">
          {howItWorks.map((item, idx) => (
            <motion.div
              key={item.title}
              className="flex items-start gap-3 p-4 rounded-2xl bg-[#0a0a0a] border border-white/5"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + idx * 0.08 }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <item.icon size={20} weight="duotone" style={{ color: item.color }} />
              </div>
              <div>
                <p className="font-medium text-white text-sm">{item.title}</p>
                <p className="text-white/50 text-xs mt-0.5 leading-snug">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Story activation flow */}
        <div className="mt-5 p-4 rounded-2xl border border-[#FFD700]/25 bg-gradient-to-br from-[#FFD700]/8 via-transparent to-[#0ABAB5]/5">
          <p className="text-sm font-semibold text-white mb-1">{t('vipShareTitle')}</p>
          <p className="text-xs text-white/55 mb-3 leading-relaxed">{t('vipShareInstruction')}</p>
          {activationSent ? (
            <>
              <div className="flex items-center gap-2 text-sm text-[#0ABAB5] font-semibold p-3 rounded-xl bg-[#0ABAB5]/10 border border-[#0ABAB5]/30 mb-3">
                <Sparkle size={16} weight="fill" />
                {t('vipActivated')}
              </div>
              {/* Allow the customer to regenerate / re-share their VIP image any time after activation. */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onPickPlatform('instagram')}
                  className="flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCB045] text-white font-semibold text-sm active:scale-[0.98] transition"
                  data-testid="vip-share-instagram"
                >
                  <InstagramLogo size={18} weight="bold" />
                  Instagram
                </button>
                <button
                  type="button"
                  onClick={() => onPickPlatform('tiktok')}
                  className="flex items-center justify-center gap-2 h-11 rounded-xl bg-black border border-white/15 text-white font-semibold text-sm active:scale-[0.98] transition"
                  data-testid="vip-share-tiktok"
                >
                  <TiktokLogo size={18} weight="bold" />
                  TikTok
                </button>
              </div>
              {storyPlatform && (
                <motion.div
                  className="mt-3 space-y-2"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Iter15 Task 12 — removed the large plate share-preview image to
                      keep the VIP page simple. Sharing/download still works via the blob. */}
                  {shareImageUrl && (
                    <p className="text-[10px] text-[#0ABAB5] text-center">{t('vipShareImageReady')}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={tryWebShare}
                      disabled={!shareImageBlob}
                      className="h-11 rounded-xl bg-[#0ABAB5] hover:bg-[#089994] text-black font-semibold text-xs"
                      data-testid="vip-share-now-btn"
                    >
                      <Share size={16} className="mr-1.5" weight="bold" />
                      {t('vipShareNow')}
                    </Button>
                    <Button
                      onClick={() => {
                        if (!shareImageUrl) return;
                        const a = document.createElement('a');
                        a.href = shareImageUrl;
                        a.download = 'autospa5-vip.png';
                        a.click();
                      }}
                      disabled={!shareImageUrl}
                      variant="outline"
                      className="h-11 rounded-xl border-white/15 bg-white/5 hover:bg-white/10 text-white text-xs"
                      data-testid="vip-share-download-btn"
                    >
                      <DownloadSimple size={16} className="mr-1.5" />
                      {t('vipDownloadShareImage')}
                    </Button>
                  </div>
                </motion.div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onPickPlatform('instagram')}
                  className={`flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCB045] text-white font-semibold text-sm active:scale-[0.98] transition ${
                    storyPlatform === 'instagram' ? 'ring-2 ring-white/70' : ''
                  }`}
                  data-testid="vip-share-instagram"
                >
                  <InstagramLogo size={18} weight="bold" />
                  Instagram
                </a>
                <a
                  href="https://www.tiktok.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onPickPlatform('tiktok')}
                  className={`flex items-center justify-center gap-2 h-11 rounded-xl bg-black border border-white/15 text-white font-semibold text-sm active:scale-[0.98] transition ${
                    storyPlatform === 'tiktok' ? 'ring-2 ring-[#25F4EE]' : ''
                  }`}
                  data-testid="vip-share-tiktok"
                >
                  <TiktokLogo size={18} weight="bold" />
                  TikTok
                </a>
              </div>

              {/* Iter15 Task 12 — removed the bottom large plate share-preview to
                  declutter. Share/Download buttons still work via the blob. */}
              {storyPlatform && (
                <motion.div
                  className="mt-3 space-y-2"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {shareImageUrl && (
                    <p className="text-[10px] text-[#0ABAB5] text-center">{t('vipShareImageReady')}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={tryWebShare}
                      disabled={!shareImageBlob}
                      className="h-11 rounded-xl bg-[#0ABAB5] hover:bg-[#089994] text-black font-semibold text-xs"
                      data-testid="vip-share-now-btn"
                    >
                      <Share size={16} className="mr-1.5" weight="bold" />
                      {t('vipShareNow')}
                    </Button>
                    <Button
                      onClick={() => {
                        if (!shareImageUrl) return;
                        const a = document.createElement('a');
                        a.href = shareImageUrl;
                        a.download = 'autospa5-vip.png';
                        a.click();
                      }}
                      disabled={!shareImageUrl}
                      variant="outline"
                      className="h-11 rounded-xl border-white/15 bg-white/5 hover:bg-white/10 text-white text-xs"
                      data-testid="vip-share-download-btn"
                    >
                      <DownloadSimple size={16} className="mr-1.5" />
                      {t('vipDownloadShareImage')}
                    </Button>
                  </div>

                  <input
                    type="url"
                    value={videoLink}
                    onChange={(e) => setVideoLink(e.target.value)}
                    placeholder={t('vipStoryLinkPlaceholder')}
                    className="w-full h-11 px-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#FFD700]/50 focus:outline-none"
                    data-testid="vip-story-link-input"
                  />
                  <Button
                    onClick={handleStoryPosted}
                    className="w-full h-11 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold rounded-xl"
                    data-testid="vip-story-posted-btn"
                  >
                    {t('vipIPostedStory')}
                  </Button>
                </motion.div>
              )}

              <p className="mt-3 text-xs font-bold text-[#FF5A00] leading-relaxed">
                {t('vipNoStoryNoCard')}
              </p>
            </>
          )}
        </div>

        {/* Footer disclaimer */}
        <p className="mt-5 text-center text-xs text-white/30 leading-relaxed px-3">
          {t('vipDisclaimer')}
        </p>
      </div>
    </div>
  );
}
